import React from 'react';
import { AnnotatedCodeExample, SimpleCodeExample } from '../../types';
import { generateAnnotatedCode, createSimpleRustExample } from '../../utils/codeTemplateParser';

/**
 * CodeRenderer Component
 * 
 * Converts structured JSON data (AnnotatedCodeExample) to HTML for the SyntaxLensWidget.
 * This is the bridge between editable JSON lens data and the existing widget.
 * 
 * Data flow: JSON → CodeRenderer → HTML with data-* attributes → SyntaxLensWidget
 */
interface CodeRendererProps {
  data: AnnotatedCodeExample;
  className?: string;
}

export const CodeRenderer: React.FC<CodeRendererProps> = ({ data, className = '' }) => {
  const renderToken = (token: typeof data.lines[0]['tokens'][0], index: number) => {
    // If token has annotation with static data, render with lens data
    if (token.annotation?.staticData) {
      const { surface, concept, paradigm, badge } = token.annotation.staticData;
      return (
        <span
          key={index}
          className={`${token.className || ''} hoverable`}
          data-surface={surface}
          data-concept={concept}
          data-paradigm={paradigm}
          data-badge={badge}
        >
          {token.text}
        </span>
      );
    }
    
    // Regular token with just syntax highlighting
    if (token.className) {
      return (
        <span key={index} className={token.className}>
          {token.text}
        </span>
      );
    }
    
    // Plain text (usually whitespace or punctuation)
    return <React.Fragment key={index}>{token.text}</React.Fragment>;
  };

  return (
    <div className={`font-mono text-sm space-y-0 ${className}`}>
      {data.lines.map((line, lineIndex) => (
        <div key={lineIndex} className="flex items-center hover:bg-gray-800/20">
          <span className="text-gray-500 w-8 text-right mr-4 select-none">
            {line.lineNumber}
          </span>
          <div className="flex-1">
            {line.tokens.map((token, tokenIndex) => renderToken(token, tokenIndex))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Creates a sample using the NEW template-based approach (recommended!)
 * This is much simpler and easier to maintain
 */
export function createSampleWithTemplate(): AnnotatedCodeExample {
  const simpleExample = createSimpleRustExample();
  return generateAnnotatedCode(simpleExample);
}

